/* theme.js — interactive islands. Deferred; hydrates on interaction.
 * Cart state lives in the cart island (assets/island-cart.js): adds here
 * apply optimistically through window.SydneyTaps.cart. */
/* Swallow benign promise rejections: aborted fetches and skipped cross-document
   view transitions ("Transition was skipped") are expected on fast navigations. */
addEventListener('unhandledrejection', (e) => {
  const r = e.reason;
  const name = r && (r.name || '');
  const msg = r ? String(r.message || r) : '';
  if (name === 'AbortError' || /Transition was skipped/i.test(msg)) e.preventDefault();
});
(() => {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);
  const drawerId = 'CartDrawer';
  /* The cart island (assets/island-cart.js, src/islands/cart) owns cart
     state: every add / qty / remove below applies optimistically through
     window.SydneyTaps.cart and persists in the background. Looked up at
     event time — the module loads after this file. Without it (module
     failed) the SSR forms and links are the fallback: a product form posts
     to /cart/add natively and lands on /cart. */
  const cart = () => (window.SydneyTaps && window.SydneyTaps.cart) || null;

  const openDrawer = () => {
    const c = cart(); if (c) return c.open();
    $('#' + drawerId)?.setAttribute('aria-hidden', 'false'); document.body.classList.add('drawer-open');
  };
  const closeDrawer = () => {
    const c = cart(); if (c) return c.close();
    $('#' + drawerId)?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    // full-overlay drawers uncover the header on close — if the cursor lands
    // on the cart button, its hover-open would instantly re-open the drawer.
    // header-flyouts listens for this and suppresses hover briefly.
    document.dispatchEvent(new CustomEvent('drawer:closed'));
  };

  // open cart drawer instead of navigating
  document.addEventListener('click', (e) => {
    const cartLink = e.target.closest('a[href$="/cart"], [data-cart-toggle]');
    if (cartLink && !cartLink.closest('.cart-drawer__ctas')) { e.preventDefault(); openDrawer(); return; }
    if (e.target.closest('[data-cart-close]')) { closeDrawer(); }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  // what the optimistic line paints before Shopify answers: the selected
  // variant from the section's [data-product-json] (title/price/sku/image),
  // the page title, and the media the shopper is looking at
  const parseMoney = (el) => { const d = (el?.textContent || '').replace(/[^\d]/g, ''); return d ? parseInt(d, 10) : 0; };
  function formHint(form, id) {
    const root = form.closest('[data-section-id]') || document;
    let v = null;
    let el = null;
    try {
      el = root.querySelector('[data-product-json]') || document.querySelector('[data-product-json]');
      v = (JSON.parse(el ? el.textContent : '[]') || []).find((x) => x.id === id) || null;
    } catch {}
    const title = (root.querySelector('.product__title, .qv__title') || document.querySelector('h1'))?.textContent.trim();
    const media = root.querySelector('.product__media img, .qv__media img');
    const fi = v && v.featured_image;
    const link = root.querySelector('a[href*="/products/"]');
    /* HOW THIS PRODUCT SHIPS, carried into the add (store.js teachLine).
     * [data-product-json] is the section's own script tag, and the PDP /
     * quick view print the answers of the two resolvers on it: data-bulky is
     * pallet spaces (0 = not bulky, from snippets/shipping-bulky.liquid +
     * shipping.pallets) and data-dm is per_pallet | per_supplier
     * (snippets/delivery-mode.liquid). Both were ALREADY computed a few lines
     * above the buy box to paint the "Large item" badge, so this costs
     * nothing. Without them the drawer's first frame has to guess, and it
     * guessed "not bulky" — Luke, 2026-09-20. `undefined` when the page said
     * nothing: the store must be able to tell "not bulky" from "not told". */
    const d = (el && el.dataset) || {};
    return {
      product_title: title,
      product_id: d.pid ? parseInt(d.pid, 10) : null,
      vendor: d.vendor || '',
      warehouse: d.wh || '',
      product_type: d.ptype || '',
      bulky: d.bulky == null ? undefined : parseInt(d.bulky, 10) || 0,
      delivery_mode: d.dm || '',
      price: v ? v.price : parseMoney(root.querySelector('[data-add-price]')),
      sku: v ? v.sku : '',
      variant_title: v && v.title && v.title !== 'Default Title' ? v.title : null,
      url: location.pathname.startsWith('/products/') ? location.pathname : (link ? link.getAttribute('href') : ''),
      image: fi ? fi.src : (media ? media.currentSrc || media.src : null),
      featured_image: fi ? { url: fi.src, width: fi.width, height: fi.height, alt: fi.alt || title } : null,
    };
  }
  function formItem(form) {
    const fd = new FormData(form);
    const id = parseInt(fd.get('id'), 10);
    const quantity = Math.max(1, parseInt(fd.get('quantity') || '1', 10) || 1);
    const properties = {};
    let has = false;
    for (const [k, val] of fd.entries()) {
      const m = /^properties\[(.+)\]$/.exec(k);
      if (m && val !== '') { properties[m[1]] = val; has = true; }
    }
    return { id, quantity, properties: has ? properties : null };
  }

  // add to cart: paints the line, the count, the totals and the confirmation
  // strip NOW; /cart/add.js runs behind it and a 422 rolls the line back with
  // its message in the drawer
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('product-form form, form[action$="/cart/add"]');
    if (!form) return;
    const c = cart();
    if (!c) return; // native post -> /cart
    e.preventDefault();
    const item = formItem(form);
    if (!item.id) return;
    item.hint = formHint(form, item.id);
    // Add-ons (snippets/product-addons.liquid): every checked box in this form
    // rides in the SAME add call, at the product's quantity — one waste per
    // bath, however many baths. Same optimistic paint, same 422 rollback.
    // `value` is the variant assets/product-addons.js matched to the finish
    // selected above, and `properties` stays null on purpose: the variant id
    // (and its SKU) is the entire contract with the order and with Aden.
    // variant_title rides along so the drawer's optimistic line already names
    // the finish, instead of showing the bare product title for the half
    // second before Shopify answers.
    const extras = [...form.querySelectorAll('[data-addon]:checked')].map((cb) => ({
      id: parseInt(cb.value, 10), quantity: item.quantity, properties: null,
      hint: {
        product_title: cb.dataset.title, price: cb.dataset.price, sku: cb.dataset.sku, url: cb.dataset.url, handle: cb.dataset.handle,
        // an add-on is a DIFFERENT product (a waste, a kit) with its own
        // bulkiness: snippets/product-addons.liquid runs the same two
        // resolvers over it, so its line is banded on the first frame too
        product_id: cb.dataset.pid ? parseInt(cb.dataset.pid, 10) : null,
        vendor: cb.dataset.vendor || '', warehouse: cb.dataset.wh || '', product_type: cb.dataset.ptype || '',
        bulky: cb.dataset.bulky == null ? undefined : parseInt(cb.dataset.bulky, 10) || 0,
        delivery_mode: cb.dataset.dm || '',
        variant_title: cb.dataset.variantTitle || null,
        image: cb.dataset.img || null,
        featured_image: cb.dataset.img ? { url: cb.dataset.img, width: 200, height: 200, alt: cb.dataset.title } : null,
      },
    })).filter((x) => x.id);
    /* resolves true once Shopify confirmed the add (false on a 422/rollback):
     * the buy row's stepper resets to 1 on `cart:added`, keeps the qty
     * otherwise. `cart:add-failed` is the other half, added 2026-09-21: it
     * has no listener on the PDP (a rejected add is reported by the drawer,
     * which is on screen and stays there) and one in the quick view, which
     * closes itself on the press and therefore has to be told when the press
     * did not take — ui.js brings the dialog back with the reason on it.
     * Both events are dispatched on the FORM and bubble, so a listener can
     * tell which of two buy rows in one document it is hearing. */
    c.add([item, ...extras]).then((ok) => {
      form.dispatchEvent(new CustomEvent(ok ? 'cart:added' : 'cart:add-failed', { bubbles: true, detail: item }));
    });
  });

  // card hover add-to-cart. Liquid tiles carry data-vid/nvar (product-card),
  // so a single-variant product adds with no round trip and a multi-variant
  // one opens quick view with none either. Island tiles (Typesense hits carry
  // no variant ids) keep the one product fetch, then add the same way.
  document.addEventListener('click', async (e) => {
    const atc = e.target.closest('[data-card-atc]');
    if (!atc) return;
    e.preventDefault();
    const handle = atc.dataset.handle;
    const c = cart();
    if (!c) { location.href = '/products/' + handle; return; }
    const quick = () => {
      const qv = atc.closest('.card')?.querySelector('[data-quick-view]');
      if (qv) qv.click(); else location.href = '/products/' + handle;
    };
    if (parseInt(atc.dataset.nvar || '0', 10) > 1) { quick(); return; }
    if (atc.dataset.vid) {
      c.add([{ id: atc.dataset.vid, quantity: 1, hint: {
        product_title: atc.dataset.title, price: atc.dataset.price, sku: atc.dataset.sku, url: atc.dataset.url, handle,
        // the tile carries the same two resolver answers the PDP does
        // (snippets/product-card.liquid) — a bath added from a grid must land
        // in Bulky items on its first frame, exactly as one added from its page
        product_id: atc.dataset.pid ? parseInt(atc.dataset.pid, 10) : null,
        vendor: atc.dataset.vendor || '', warehouse: atc.dataset.wh || '', product_type: atc.dataset.ptype || '',
        bulky: atc.dataset.bulky == null ? undefined : parseInt(atc.dataset.bulky, 10) || 0,
        delivery_mode: atc.dataset.dm || '',
        image: atc.dataset.img || null,
        featured_image: atc.dataset.img ? { url: atc.dataset.img, width: atc.dataset.imgw, height: atc.dataset.imgh, alt: atc.dataset.title } : null,
      } }]);
      return;
    }
    if (atc.getAttribute('aria-busy')) return;
    atc.setAttribute('aria-busy', 'true');
    try {
      const prod = await fetch('/products/' + handle + '.js').then((r) => r.json());
      const v = prod.variants[0];
      if (prod.variants.length === 1 && v.available) {
        const fi = v.featured_image || null;
        c.add([{ id: v.id, quantity: 1, hint: {
          product_title: prod.title, price: v.price, sku: v.sku, url: '/products/' + handle, handle,
          /* /products/<handle>.js carries NO metafields, so there is no
             `bulky` to pass here and the line renders unclassified (no band,
             no promise claim) until the store's Section Rendering round trip
             answers. Identity is still worth carrying: it is what that round
             trip matches the answer back onto. This path is the Typesense
             tiles only — Liquid tiles carry data-vid and never reach it. */
          product_id: prod.id != null ? parseInt(prod.id, 10) : null,
          vendor: prod.vendor || '', product_type: prod.type || '',
          image: fi ? fi.src : (prod.featured_image || (prod.images && prod.images[0]) || null),
          featured_image: fi ? { url: fi.src, width: fi.width, height: fi.height, alt: fi.alt || prod.title } : null,
        } }]);
      } else {
        quick();
      }
    } catch {
      location.href = '/products/' + handle;
    } finally { atc.removeAttribute('aria-busy'); }
  });

  // qty +/- and per-line remove are the island's own handlers now (React
  // onClick on the buttons it renders) — no delegation here, or a tap would
  // be applied twice.

  /* configurable-product prefetch: when a cfg tile nears the viewport, warm
     the App Proxy config (browser cache + Shopify CDN edge + Cloud Run
     instance — the response is public/max-age=300/swr=3600) and prefetch the
     PDP document, so the sliders click lands with everything hot */
  (() => {
    if (!('IntersectionObserver' in window)) return;
    const done = new Set();
    const warm = (el) => {
      const pid = el.dataset.cfgPrefetch;
      if (!pid || done.has(pid) || done.size >= 24) return;
      done.add(pid);
      fetch('/apps/configurator/config?product=' + pid, { credentials: 'same-origin' }).catch(() => {});
      const href = el.getAttribute('href');
      if (href) {
        const l = document.createElement('link');
        l.rel = 'prefetch'; l.as = 'document'; l.href = href.split('?')[0];
        document.head.appendChild(l);
      }
    };
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { warm(e.target); io.unobserve(e.target); }
    }), { rootMargin: '300px' });
    let t = null;
    const scan = () => document.querySelectorAll('[data-cfg-prefetch]').forEach((el) => io.observe(el));
    scan();
    // trailing THROTTLE, not debounce — pages with a steady mutation pulse
    // (async islands repainting) would push a debounce out forever
    new MutationObserver(() => { if (!t) t = setTimeout(() => { t = null; scan(); }, 200); })
      .observe(document.body, { childList: true, subtree: true });
  })();

  // PDP finish/variant swatch selection
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('.swatch--lg');
    if (!sw) return;
    sw.closest('[role="radiogroup"]')?.querySelectorAll('.swatch--lg').forEach((s) => {
      const on = s === sw;
      s.classList.toggle('is-selected', on);
      s.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  });
})();

/* ---------- megamenu hover-intent controller (safe triangle) ----------
 * Owns panel open state (.is-open on the li; CSS :focus-within still covers
 * keyboard). Best-practice set: full-height rectangular targets (CSS),
 * open-intent delay, sibling-switch SETTLE delay, close grace on leaving
 * the header, Escape closes. NOTE on safe triangles (Smashing Magazine):
 * the triangle pattern applies to narrow flyouts; for a FULL-WIDTH panel
 * the triangle from cursor to the panel's top corners covers the whole
 * navbar band, deferring legitimate sibling switches forever (verified
 * live) - and downward travel into a full-width panel cannot cross a
 * sibling anyway, so the settle delay is the correct protection here.
 * Arming guard (html.nav-armed) still gates fresh documents. */
(function () {
  var nav = document.querySelector('.site-header .nav');
  if (!nav) return;
  var items = Array.prototype.slice.call(nav.querySelectorAll('.nav__item'));
  if (!items.length) return;
  var OPEN_DELAY = 120, CLOSE_GRACE = 240, SETTLE = 140;
  var openItem = null, openTimer = null, closeTimer = null, switchTimer = null;

  function panelOf(li) { return li.querySelector('.mega, .megamenu'); }
  function anchorOf(li) { return li.querySelector('a'); }
  function setOpen(li) {
    if (openItem === li) return;
    // item-to-item switch: no fade-out/fade-in (reads as a repaint flash).
    // The old panel vanishes and the new one appears in the SAME frame
    // (.nav--switching kills the opacity transition), and the incoming panel
    // morphs its height from the outgoing panel's height so the dropdown
    // resizes fluidly instead of jumping.
    var switching = !!(openItem && li);
    var prevPanel = openItem ? panelOf(openItem) : null;
    var prevH = switching && prevPanel ? prevPanel.offsetHeight : 0;
    if (switching) nav.classList.add('nav--switching');
    if (openItem) {
      openItem.classList.remove('is-open');
      var a0 = anchorOf(openItem); if (a0) a0.setAttribute('aria-expanded', 'false');
      // tiered panels re-open as bar-only: reset expansion + active tab.
      // Instant while switching (transitions are suppressed anyway); after
      // the fade-out when closing, so the panel doesn't collapse mid-fade.
      if (prevPanel && prevPanel.classList.contains('mega--tiered')) {
        var resetTiered = (function (p_) {
          return function () {
            p_.classList.remove('is-expanded');
            p_.querySelectorAll('.mega__tab.is-active').forEach(function (t) { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
            p_.querySelectorAll('.mega__tabpanel.is-active').forEach(function (tp) { tp.classList.remove('is-active'); });
          };
        })(prevPanel);
        if (switching) resetTiered(); else setTimeout(resetTiered, 200);
      }
    }
    openItem = li;
    if (li) {
      li.classList.add('is-open');
      var a1 = anchorOf(li); if (a1 && panelOf(li)) a1.setAttribute('aria-expanded', 'true');
      var p = panelOf(li);
      /* Featured carousel (compiled megamenu): the crossfade is pure CSS
       * keyframes, which keep running against the document clock whether the
       * panel is open or not. Opening a panel five seconds in would show a
       * half-faded frame or a nearly-full progress dash. Restart the whole
       * cycle here — animation:none, force a reflow to commit it, then hand
       * the animation back — so every open begins on slide 1 at 0%. This is
       * the ONLY thing JS does for the carousel; timing, pausing and the
       * reduced-motion case all live in CSS. */
      var car = p ? p.querySelector('[data-mega-carousel]') : null;
      if (car) {
        var anim = car.querySelectorAll('.mega__slide, .mega__dash-fill');
        anim.forEach(function (el) { el.style.animation = 'none'; });
        void car.offsetHeight;
        anim.forEach(function (el) { el.style.animation = ''; });
      }
      if (switching && p && prevH) {
        var targetH = p.offsetHeight;
        if (Math.abs(targetH - prevH) > 2) {
          p.style.height = prevH + 'px';
          p.style.overflow = 'hidden';
          void p.offsetHeight; // commit start height before transitioning
          p.style.transition = 'height .18s cubic-bezier(.16,1,.3,1)';
          p.style.height = targetH + 'px';
          var done = function () {
            p.style.transition = ''; p.style.height = ''; p.style.overflow = '';
            p.removeEventListener('transitionend', done);
          };
          p.addEventListener('transitionend', done);
          setTimeout(done, 260); // safety if transitionend never fires
        }
      }
    }
    if (switching) requestAnimationFrame(function () {
      requestAnimationFrame(function () { nav.classList.remove('nav--switching'); });
    });
  }

  items.forEach(function (li) {
    li.addEventListener('pointerenter', function () {
      clearTimeout(closeTimer);
      var hasMega = !!panelOf(li);
      if (!openItem) {
        if (hasMega) openTimer = setTimeout(function () { setOpen(li); }, OPEN_DELAY);
        return;
      }
      if (openItem === li) { clearTimeout(switchTimer); return; }
      clearTimeout(switchTimer);
      switchTimer = setTimeout(function () {
        if (li.matches(':hover')) setOpen(hasMega ? li : null);
      }, SETTLE);
    });
    li.addEventListener('pointerleave', function () { clearTimeout(openTimer); });
  });

  var header = document.querySelector('.site-header');
  if (header) {
    header.addEventListener('pointerleave', function () {
      clearTimeout(switchTimer);
      closeTimer = setTimeout(function () { setOpen(null); }, CLOSE_GRACE);
    });
    header.addEventListener('pointerenter', function () { clearTimeout(closeTimer); });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openItem) setOpen(null);
  });
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.nav__item a, .mega a, .megamenu a')) setOpen(null);
  }, true);
})();

/* ---------- tabbed megamenu (mega-menu-17 variant) tab switching ---------- */
(function () {
  var settle = null;
  function activate(tabEl) {
    var mega = tabEl.closest('.mega--tabbed');
    if (!mega) return;
    // tiered style opens collapsed (tab bar only) — first activation expands
    mega.classList.add('is-expanded');
    mega.querySelectorAll('.mega__tab').forEach(function (t) {
      var on = t === tabEl;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    var idx = tabEl.getAttribute('data-mega-tab');
    mega.querySelectorAll('.mega__tabpanel').forEach(function (p_) {
      p_.classList.toggle('is-active', p_.getAttribute('data-mega-panel') === idx);
    });
  }
  document.addEventListener('pointerover', function (e) {
    var tab = e.target.closest && e.target.closest('.mega__tab');
    if (!tab) return;
    clearTimeout(settle);
    settle = setTimeout(function () { activate(tab); }, 100);
  });
  document.addEventListener('pointerout', function () { clearTimeout(settle); });
  document.addEventListener('click', function (e) {
    var tab = e.target.closest && e.target.closest('.mega__tab');
    if (tab) activate(tab);
  });
  document.addEventListener('focusin', function (e) {
    var tab = e.target.closest && e.target.closest('.mega__tab');
    if (tab) activate(tab);
  });
})();

/* ---------- header scroll behaviour -------------------------------------
 * Two jobs, both consequences of the header finally pinning (see the CSS block
 * in theme.css for why it never did before).
 *
 * 1. --header-h is declared as a flat 76px in theme.liquid while the bar
 *    actually renders at 65px (64px + 1px border; 57px at <=900px). The value
 *    being 11px too LARGE meant every offset derived from it — .plp__rail,
 *    .cfg__side, the PDP dossier rail — reserved too much room and sat content
 *    too low. (An earlier version of this comment said content "tucks under the
 *    bar"; that was backwards.) It never mattered while the header scrolled
 *    away. It does now. Measure it.
 *
 * 2. The reveal mode. Deliberately NOT hidden while anything anchored to the
 *    header is open, and deliberately deaf to programmatic scrolls.
 */
(function () {
  var root = document.documentElement;
  var header = document.querySelector('.site-header');
  if (!header) return;

  var lastH = 0;
  function measure() {
    var h = Math.round(header.getBoundingClientRect().height);
    if (!h || h === lastH) return;
    lastH = h;
    root.style.setProperty('--header-h', h + 'px');
    // ui.js recomputes --rail-top on window resize only, and ResizeObserver
    // callbacks run AFTER resize listeners — so crossing the 900px breakpoint
    // (bar 64 -> 56) would otherwise leave the rail pinned off the old value
    // until the next resize. Tell it the header changed.
    window.dispatchEvent(new Event('resize'));
  }
  // The tabbed header's scroll modes (sections/header.liquid stamps
  // data-hdr-mode on <html> for every mode except hide-all) own BOTH jobs:
  // header-tabbed.js measures --header-h from the rows that are actually
  // pinned (the strip alone can be 48px while this element's box stays at its
  // rest height) and runs its own scroll controller. Measuring here would
  // overwrite that with the rest height on every ResizeObserver tick.
  if (root.hasAttribute('data-hdr-mode')) return;

  measure();
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header);
  window.addEventListener('load', measure);

  if (root.getAttribute('data-header-scroll') !== 'reveal') return;

  var lastY = window.scrollY;
  var ticking = false;
  var primed = false;          // ignore the first scroll event after load
  var FLOOR = 120;             // at/near the top, always show
  var DELTA = 6;               // trackpad and sub-pixel jitter

  function anchoredOverlayOpen() {
    // The search overlay was RETIRED (layout/theme.liquid: "universal header
    // bar + inline grid"), so checking for it could never fire. Live search
    // state is `is-searching`, and the header holds the input being typed into.
    return document.body.classList.contains('drawer-open')
      || header.classList.contains('is-searching')
      || root.classList.contains('is-searching')
      || !!document.querySelector('.nav__item.is-open')
      // tabbed header (header-tabbed.js): a tier-3 panel is open
      || header.classList.contains('is-menu-open')
      // search dialog (search-dialog.js) hangs off the bar
      || header.classList.contains('is-search-open')
      // header flyouts (account/project) open on hover with no scroll lock and
      // no focusin, so nothing else here would catch them
      || !!header.querySelector('[data-flyout]:not([hidden])');
  }

  function onScroll() {
    var y = window.scrollY;
    var diff = y - lastY;
    ticking = false;
    if (!primed) { primed = true; lastY = y; return; }
    if (Math.abs(diff) < DELTA) return;
    // Programmatic jumps are not intent. The PLP island calls scrollIntoView on
    // EVERY refinement (src/islands/plp/App.jsx, typesense-plp.js scrollTo), so
    // without this the header vanished every time a filter was clicked.
    if (Math.abs(diff) > window.innerHeight * 0.5) { lastY = y; return; }
    if (y <= FLOOR || anchoredOverlayOpen()) root.classList.remove('header-hidden');
    else if (diff > 0) root.classList.add('header-hidden');
    else root.classList.remove('header-hidden');
    lastY = y;
  }

  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(onScroll);
  }, { passive: true });

  // Arriving with the bar already hidden reads as a broken page: bfcache
  // restores the class AND lastY, and a non-bfcache back/forward restores
  // scroll in one large positive jump.
  window.addEventListener('pageshow', function () {
    root.classList.remove('header-hidden');
    lastY = window.scrollY;
    primed = false;
  });

  // a hidden header must never hold focus
  document.addEventListener('focusin', function (e) {
    if (header.contains(e.target)) root.classList.remove('header-hidden');
  });
})();
